'use client';

import { useState, useEffect } from 'react';

interface TemporalLabels {
  today: string;
  tomorrow: string;
  yesterday: string;
  days: string;
  week: string;
  weeks: string;
  month: string;
  months: string;
}

interface TemporalProximityProps {
  dateStart: string;
  lang: string;
  pastLabel?: string;
  futureLabel?: string;
  labels?: TemporalLabels;
}

function formatRelativeDate(dateStart: string, labels: TemporalLabels): { text: string; isPast: boolean; daysAway: number } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(dateStart + 'T00:00:00');
  const diffMs = target.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / 86400000);

  if (diffDays === 0) return { text: labels.today, isPast: false, daysAway: 0 };
  if (diffDays === 1) return { text: labels.tomorrow, isPast: false, daysAway: 1 };
  if (diffDays === -1) return { text: labels.yesterday, isPast: true, daysAway: 1 };

  const absDays = Math.abs(diffDays);
  if (absDays < 7) {
    return { text: `${absDays} ${labels.days}`, isPast: diffDays < 0, daysAway: absDays };
  }
  const w = Math.round(absDays / 7);
  if (absDays < 30) {
    return { text: `${w} ${w > 1 ? labels.weeks : labels.week}`, isPast: diffDays < 0, daysAway: absDays };
  }
  const m = Math.round(absDays / 30);
  if (absDays < 365) {
    return { text: `${m} ${m > 1 ? labels.months : labels.month}`, isPast: diffDays < 0, daysAway: absDays };
  }
  return { text: '', isPast: diffDays < 0, daysAway: absDays };
}

const DEFAULT_LABELS: TemporalLabels = {
  today: 'Today', tomorrow: 'Tomorrow', yesterday: 'Yesterday',
  days: 'days', week: 'week', weeks: 'weeks', month: 'month', months: 'months',
};

export function TemporalProximity({ dateStart, pastLabel = 'ago', futureLabel = 'from now', labels }: TemporalProximityProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted) return null;

  const l = labels || DEFAULT_LABELS;
  const { text, isPast, daysAway } = formatRelativeDate(dateStart, l);
  if (!text) return null;

  const isToday = daysAway === 0;
  const isSoon = !isPast && daysAway <= 30;

  const fullText = isToday ? text
    : text === l.tomorrow ? text
    : text === l.yesterday ? text
    : isPast ? `${text} ${pastLabel}` : `${text} ${futureLabel}`;

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
      isToday ? 'bg-semantic-error/10 text-semantic-error' :
      isSoon ? 'bg-semantic-warning/10 text-semantic-warning' :
      isPast ? 'bg-surface-hover text-text-dim' :
      'bg-brand/10 text-brand'
    }`}>
      {isToday && (
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-semantic-error opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-semantic-error" />
        </span>
      )}
      {fullText}
      <span className="sr-only"> ({dateStart})</span>
    </span>
  );
}
